import { useEffect, useId, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import type {
  MundialPredictionEvent,
  MundialPredictionAnswer,
} from "@/lib/predictions/mundialEngine";
import { shouldUseFoundationFallback, MOCK_PREDICTION_EVENTS } from "@/lib/demo/executiveDemoMock";

/**
 * Hook that loads open prediction events for a given match and the current
 * user's answers, with Supabase realtime updates. Returns null events when
 * matchId is not yet known.
 */
export function useMundialPredictions(matchId: string | undefined): {
  events: MundialPredictionEvent[];
  answers: Record<string, MundialPredictionAnswer>;
  loading: boolean;
  refresh: () => Promise<void>;
  totalXp: number;
} {
  const [events, setEvents] = useState<MundialPredictionEvent[]>([]);
  const [answers, setAnswers] = useState<Record<string, MundialPredictionAnswer>>({});
  const [loading, setLoading] = useState(true);
  // Unique per hook instance — prevents "cannot add postgres_changes
  // callbacks after subscribe()" when multiple components mount this hook
  // with the same matchId (Supabase reuses channels by topic name).
  const instanceId = useId().replace(/[^a-zA-Z0-9]/g, "");

  const load = useCallback(async () => {
    if (!matchId) {
      setEvents([]);
      setAnswers({});
      setLoading(false);
      return;
    }
    setLoading(true);
    const nowIso = new Date().toISOString();
    const { data: evs } = await supabase
      .from("prediction_events")
      .select("*")
      .eq("match_id", matchId)
      .in("status", ["open", "resolved"])
      .gte("end_time", new Date(Date.now() - 10 * 60_000).toISOString())
      .order("end_time", { ascending: true })
      .limit(20);
    let evList = (evs ?? []) as MundialPredictionEvent[];
    // P0-FIX-01 — Reconnect UI ↔ Engine.
    // The Mundial Prediction Engine writes match_id as `live-<apiFootballFixtureId>`,
    // but the local hub passes slug-style match ids (e.g. `mex-arg`). When the
    // strict per-match lookup returns nothing, fall back to the currently open
    // events globally so the floating card surfaces real predictions instead
    // of staying invisible. This is additive: real per-match matches still
    // win when they exist.
    if (evList.length === 0) {
      const { data: anyOpen } = await supabase
        .from("prediction_events")
        .select("*")
        .in("status", ["open", "resolved"])
        .gte("end_time", new Date(Date.now() - 5 * 60_000).toISOString())
        .order("end_time", { ascending: true })
        .limit(5);
      evList = (anyOpen ?? []) as MundialPredictionEvent[];
    }
    if (evList.length === 0 && shouldUseFoundationFallback()) {
      // Surface mock events for the requested matchId (or fall back to all
      // mock events so the demo never shows an empty Prediction Pulse panel).
      const forMatch = MOCK_PREDICTION_EVENTS.filter((e) => e.match_id === matchId);
      evList = (forMatch.length > 0 ? forMatch : MOCK_PREDICTION_EVENTS).slice(0, 20);
    }
    setEvents(evList);

    const { data: auth } = await supabase.auth.getUser();
    if (auth.user && evList.length > 0) {
      const { data: ans } = await supabase
        .from("prediction_answers")
        .select("*")
        .eq("user_id", auth.user.id)
        .in("prediction_event_id", evList.map((e) => e.id));
      const map: Record<string, MundialPredictionAnswer> = {};
      for (const a of (ans ?? []) as MundialPredictionAnswer[]) {
        map[a.prediction_event_id] = a;
      }
      setAnswers(map);
    } else {
      setAnswers({});
    }
    setLoading(false);
    void nowIso;
  }, [matchId]);

  useEffect(() => { void load(); }, [load]);

  // Realtime: refresh when events for this match change.
  useEffect(() => {
    if (!matchId) return;
    const ch = supabase
      .channel(`mundial-pred-${matchId}-${instanceId}`)
      .on("postgres_changes",
        { event: "*", schema: "public", table: "prediction_events", filter: `match_id=eq.${matchId}` },
        () => { void load(); })
      .on("postgres_changes",
        { event: "*", schema: "public", table: "prediction_answers" },
        () => { void load(); })
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [matchId, load, instanceId]);

  const totalXp = Object.values(answers).reduce((s, a) => s + (a.points_awarded ?? 0), 0);

  return { events, answers, loading, refresh: load, totalXp };
}